import React, { useState } from "react";
import axios from "axios";
import { Search, Mic, AlertTriangle, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import host from "../../global.jsx";

// Maps a stored record to a plain-English diagnosis — the whole point of this
// page: support reads the reason, not raw telemetry.
const diagnoseTest = (a) => {
  const verdict = a.verdict || (a.result === "success" ? "PASSED" : null);
  const mutedAtEnd = a.track_end?.muted === true;
  switch (verdict) {
    case "PASSED": {
      const ms = a.audio?.first_voiced_ms;
      return { tone: "ok", text: `Mic working — speech detected${ms != null ? ` in ${ms}ms` : ""}.` };
    }
    case "SIGNAL_DEAD":
      return {
        tone: "bad",
        text: `"${a.tested_device_label || "Device"}" delivered no audio signal at all — muted at OS/hardware level, dead driver, or not a real mic (e.g. Stereo Mix).`,
      };
    case "NO_SPEECH":
      if (mutedAtEnd)
        return {
          tone: "bad",
          text: "Mic reports MUTED (hardware mute switch / OS mute) — pipeline alive but nothing voiced. Candidate saw 'speak aloud'.",
        };
      return {
        tone: "warn",
        text: "Pipeline alive but no speech crossed the threshold — candidate silent, too far, or too quiet.",
      };
    case "DEVICE_ERROR": {
      const code = a.error_code || a.permission_error || "";
      if (/NotReadable|TrackStart/i.test(code))
        return { tone: "bad", text: "Mic locked by another app (Zoom/Teams/OBS holding the device)." };
      if (/NotAllowed|Security/i.test(code))
        return { tone: "bad", text: "Candidate denied mic permission (or browser blocked it)." };
      if (/NotFound/i.test(code))
        return { tone: "bad", text: "No microphone found on the machine." };
      if (/suspended/i.test(code) || /suspended/i.test(a.failure_reason || ""))
        return { tone: "bad", text: "Browser audio engine refused to start (autoplay/gesture restriction)." };
      return { tone: "bad", text: `Device error: ${code || a.failure_reason || "unknown"}.` };
    }
    case "ABORTED":
      return {
        tone: "muted",
        text: `Test killed mid-run (${a.abort_reason || "page navigation / tab close"}) — candidate never saw a result.`,
      };
    default:
      // v1 record (old client)
      return {
        tone: a.result === "success" ? "ok" : "warn",
        text: `Legacy record — result: ${a.result || a.test_result || "?"}${a.failure_reason ? ` (${a.failure_reason})` : ""}.`,
      };
  }
};

const diagnoseAnswer = (a) => {
  if (a.ended) return { tone: "bad", text: "Device DIED mid-answer (track ended) — recording cut off." };
  if ((a.zero_windows || 0) > 0)
    return { tone: "warn", text: `${a.zero_windows} silent window(s) of 3s+ pure zeros while recording — possible OS mute mid-answer.` };
  if ((a.mute_events || 0) > 0)
    return { tone: "warn", text: `Browser fired ${a.mute_events} mute event(s) during the answer.` };
  return { tone: "ok", text: "Recorded cleanly." };
};

// Portal visual language: translucent dark cards + left accent border per tone
// (same pattern the Feedback page uses for positive/negative highlights).
const toneStyles = {
  ok: "border-l-4 border-l-green-400 text-green-300",
  warn: "border-l-4 border-l-amber-400 text-amber-300",
  bad: "border-l-4 border-l-red-400 text-red-300",
  muted: "border-l-4 border-l-gray-500 text-gray-400",
};

const VerdictIcon = ({ tone }) =>
  tone === "ok" ? (
    <CheckCircle2 className="w-4 h-4 shrink-0" />
  ) : tone === "muted" ? (
    <MinusCircle className="w-4 h-4 shrink-0" />
  ) : tone === "bad" ? (
    <XCircle className="w-4 h-4 shrink-0" />
  ) : (
    <AlertTriangle className="w-4 h-4 shrink-0" />
  );

const recordTime = (a) => {
  const t = a.timestamp || a.client_timestamp || a.server_timestamp;
  if (!t) return "";
  try {
    return new Date(t).toLocaleString();
  } catch (_) {
    return String(t);
  }
};

const MicHealthTracker = () => {
  const [interviewId, setInterviewId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const lookup = async () => {
    const id = interviewId.trim();
    if (!id) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.get(`${host}/geniehireadmin/mic-health/${id}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 401 ? "Not authorized — log in to the admin portal." : "Lookup failed — check the interview id and try again.");
    } finally {
      setLoading(false);
    }
  };

  const attempts = data?.attempts || [];
  const tests = attempts.filter((a) => !a.record_type);
  const answers = attempts.filter((a) => a.record_type === "answer_health");
  const sessions = data?.sessions || [];
  const lastBrowser =
    [...tests].reverse().find((a) => a.device_info?.browser)?.device_info || null;
  const lostSessions = Object.entries(data?.lost_records || {});

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Mic className="w-7 h-7 text-pink-400" />
        <div>
          <h1 className="text-2xl font-semibold text-white">Mic Health Tracker</h1>
          <p className="text-sm text-gray-400">
            Full microphone history for an interview — every test, every answer, with the reason in plain English.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-8">
        <input
          value={interviewId}
          onChange={(e) => setInterviewId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="Interview ID (e.g. 6a4f4d7031adc87430f92d55)"
          className="flex-1 px-4 py-2 bg-transparent border border-gray-700/50 rounded-lg text-[#CCCCCC] placeholder-gray-400 text-sm focus:outline-none"
        />
        <button
          onClick={lookup}
          disabled={loading || !interviewId.trim()}
          className="px-6 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Search className="w-4 h-4" />
          {loading ? "Looking up…" : "Look up"}
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-600/50 backdrop-blur-sm border-l-4 border-l-red-400 text-red-300 text-sm">
          {error}
        </div>
      )}

      {data && data.found === false && (
        <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-600/50 backdrop-blur-sm text-gray-300 text-sm">
          No mic logs exist for this interview id — the candidate has not reached the mic test yet.
        </div>
      )}

      {data && data.found && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              ["Mic tests", tests.length],
              ["Answers monitored", answers.length],
              ["Page loads (sessions)", Math.max(sessions.length, Object.keys(data.lost_records || {}).length) || "—"],
              ["Browser", lastBrowser ? `${lastBrowser.browser} · ${lastBrowser.os}` : "—"],
            ].map(([label, value]) => (
              <div key={label} className="bg-white/10 p-4 rounded-lg shadow-lg border border-gray-600/30 backdrop-blur-sm">
                <div className="text-xs text-gray-400">{label}</div>
                <div className="text-lg font-semibold text-white truncate">{value}</div>
              </div>
            ))}
          </div>

          {lostSessions.length > 0 && (
            <div className="mb-4 p-4 rounded-lg bg-gray-800/30 border border-gray-600/50 backdrop-blur-sm border-l-4 border-l-amber-400 text-amber-300 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Transport loss detected:{" "}
                {lostSessions
                  .map(([sid, seqs]) => `session ${sid.slice(0, 8)} lost record(s) #${seqs.join(", #")}`)
                  .join(" · ")}{" "}
                — these never reached the database (tab closed mid-send or server unreachable).
              </span>
            </div>
          )}

          {/* Timeline */}
          <div className="space-y-3">
            {attempts.map((a, i) => {
              const isAnswer = a.record_type === "answer_health";
              const isBlob = a.record_type === "answer_blob_suspect";
              const d = isAnswer
                ? diagnoseAnswer(a)
                : isBlob
                ? { tone: "warn", text: `Suspiciously small recording (${a.blob_size ?? "?"} bytes) — likely dead audio.` }
                : diagnoseTest(a);
              return (
                <div
                  key={a.record_id || a.attempt_id || i}
                  className={`p-4 rounded-lg bg-gray-800/30 border border-gray-600/50 backdrop-blur-sm ${toneStyles[d.tone]}`}
                >
                  <div className="flex items-center justify-between gap-3 text-xs text-gray-400 mb-1">
                    <span className="font-medium uppercase tracking-wide">
                      {isAnswer
                        ? `Answer · question ${a.question_index != null ? a.question_index + 1 : "?"}`
                        : isBlob
                        ? "Answer upload check"
                        : `Mic test${a.trigger ? ` · ${a.trigger}` : ""}${a.verdict ? ` · ${a.verdict}` : ""}`}
                    </span>
                    <span>{recordTime(a)}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <VerdictIcon tone={d.tone} />
                    <span>{d.text}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-400 flex flex-wrap gap-x-4">
                    {(a.tested_device_label || a.device_label) && (
                      <span>Device: {a.tested_device_label || a.device_label}</span>
                    )}
                    {a.test_duration_ms != null && <span>{(a.test_duration_ms / 1000).toFixed(1)}s</span>}
                    {a.duration_ms != null && <span>{(a.duration_ms / 1000).toFixed(1)}s</span>}
                    {a.visibility?.hidden_ms > 0 && <span>tab hidden {a.visibility.hidden_ms}ms during test</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default MicHealthTracker;
